// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, ElementRef, AfterViewInit, OnDestroy, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import 'pixi.js/unsafe-eval';
import * as PIXI from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { Analysis } from "../../../../interfaces/analysis";
import { HceAlignmentViewRequest } from '../../../../interfaces/hce/hce-alignment-view';
import { Sv } from '../../../../interfaces/hce/hce-sv';
import { HceView } from '../../../../interfaces/hce/hce-reference';
import { HceAnnotation } from '../../../../interfaces/hce/hce-annotation';
import { HceContig, HceRefGroup, HceRefGroupMap, HceChrMap, EdgeVal } from '../../../../interfaces/hce/hce-results-summary-view';
import { HceService } from '../../../../services/hce/hce.service';
import { HceResultsSummaryViewService } from '../../../../services/hce/hce-results-summary-view/hce-results-summary-view.service';
import { MessageModalService } from '../../../../services/message-modal/message-modal.service';

/* ========= Type Utility ========= */
type hasVisible = { visibled: boolean, highlight: boolean }
type drawSV = Sv & hasVisible
type drawAnnotation = HceAnnotation & hasVisible
type SvHoverEntry = {
  minKb: number;
  maxKb: number;
  rank: number;
  svid: number;
  block: string;
};

/* ========= Layout constants (from top: Contigs → References → SV → annotation) ========= */
interface Lane { y: number; h: number; }
interface Layout {
  ruler: number;
  label: number;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  gap: number;
  contigs: Lane;
  references: Lane;
  sv: Lane;
  annotation: Lane;
  hasAnno: number;
  chrH: number;
  worldKb: number;
}

const LAYOUT: Layout = {
  ruler: 70,
  label: 70,
  marginTop: 12,
  gap: 6,
  contigs: { y: 0, h: 10 },
  references: { y: 0, h: 10 },
  sv: { y: 0, h: 10 },
  annotation: { y: 0, h: 10 },
  hasAnno: 0,
  marginRight: 20,
  marginBottom: 12,
  chrH: 0,
  worldKb: 0,
};

/* ========= Definition of appearance ========= */
const SV_COLORS: Record<string, string> = {
  Deletion: "rgb(192, 80, 77)",
  Insertion: "rgb(79, 129, 189)",
  Inversion: "rgb(155, 187, 89)",
  InDel: "rgb(255, 153, 50)",
  Translocation: "rgb(162, 40, 255)",
  Duplication: "rgb(116, 192, 242)",
  default: "rgb(221, 221, 221)",
};

const GIE_STAIN_COLOR_MAP: Record<string, string> = {
  gneg: "rgb(255,255,255)",
  gpos: "rgb(0,0,0)",
  acen: "rgb(217,47,39)",
  gvar: "rgb(220,220,220)",
  gpos100: "rgb(0,0,0)",
  gpos75: "rgb(130,130,130)",
  gpos66: "rgb(130,130,130)",
  gpos50: "rgb(160,160,160)",
  gpos33: "rgb(210,210,210)",
  gpos25: "rgb(200,200,200)",
  stalk: "rgb(100,127,164)",
  other: "rgb(221,221,221)",
};

@Component({
  selector: 'app-chromosome-view',
  standalone: true,
  templateUrl: './chromosome-view.component.html',
  styleUrl: './chromosome-view.component.css'
})
export class ChromosomeViewComponent implements AfterViewInit, OnDestroy {
  @ViewChild('host', { static: false }) hostRef!: ElementRef<HTMLDivElement>;
  @ViewChild('hScroller', { static: false }) hScrollerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('vScroller', { static: false }) vScrollerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('hSpacer', { static: false }) hSpacerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('vSpacer', { static: false }) vSpacerRef?: ElementRef<HTMLDivElement>;
  @Input() filterSvList!: Sv[];
  @Input() contigs!: HceContig[];
  @Input() references!: HceView[];
  @Input() refsMappingTable!: HceChrMap;
  @Input() filterRefNameMap!: HceChrMap;
  @Input() annotations: Record<string, HceAnnotation[]> = {};
  @Input() db!: string;
  @Input() analysis!: Analysis | null;
  @Output() initialized = new EventEmitter<void>();

  app!: PIXI.Application;
  viewport!: Viewport;
  private viewportRoot!: PIXI.Container;
  private worldMask?: PIXI.Graphics;

  // Layer (Z-order: Contigs → References → SV → annotation → events)
  private layers!: {
    contigs: PIXI.Container;
    references: PIXI.Container;
    svs: PIXI.Container;
    annotations: PIXI.Container;
    events: PIXI.Container;
  };
  private hudSVLabels!: PIXI.Container;       // SV Label (Genes)
  private hudFollow!: PIXI.Container;         // Follow HUD
  private hudReferenceFramesG?: PIXI.Graphics;  // Reference HUD
  private hudFixed!: PIXI.Container;          // Fixed HUD
  private hudRuler!: PIXI.Container;          // Ruler
  private hudcomments!: PIXI.Container;       // Top-right Image / Comment
  private hudTooltip!: PIXI.Container;        // Tooltip

  private readonly RULER_Y = 60;              // Offset from the top of the screen
  private readonly RULER_BAND_H = 80;         // Height of the white band
  private readonly Label_X = 60;              // White band of the label
  private readonly LABEL_ABOVE_PAD_Y = 10;    // Upward offset from the baseline (px)
  private readonly ANNO_LINE_H = 10;

  // hudcomments
  private readonly HUD_IMG_Y = 10;            // Margin from the top right (px)
  private readonly HUD_IMG_MAX_W = 120;       // Maximum display width
  private readonly HUD_IMG_MAX_H = 48;        // Maximum display height
  private readonly HUD_IMG_GAP = 10;          // Spacing for each element (px)
  private readonly CREDIT_BELLOW_PAD_Y = 57;  // Downward offset from the reference point (px)

  // Scale line (protruding up and down)
  private readonly TICK_UP_H = 6;             // Upward from the baseline (px)
  private readonly TICK_DOWN_H = 6;           // Downward from the baseline (px)
  private readonly TICK_COLOR = 0x666666;
  private readonly RULER_COLOR = 0x333333;

  // Dynamic chopping adjustment knob
  private readonly MIN_TICK_PX = 40;          // Minimum pixel interval of the scale marks (becomes sparse if increased)
  private readonly MIN_LABEL_PX = 40;         // Minimum distance between labels (px, will be thinned if narrow)
  private readonly MIN_RULER_STEP_KB = 1;     // = 0.001 Mb

  // hybrid fixation: retained for the application of Y pan differences
  private hudLabels: { node: PIXI.Text; worldY: number }[] = [];
  private hudSeps: { node: PIXI.Graphics; worldY: number }[] = [];
  private hudAnnoFileLabels: { node: PIXI.Text; worldY: number; filename: string }[] = [];

  // Scale（kb→px）
  private baseRate = 0.01;
  private minScale = 1;
  private maxScale = 20000;

  // data
  private _sampleName: string | null = null;
  private _selectedSample: string | null = null;
  private _hideSharedSv: boolean = false;
  private _refsMappingTable: HceChrMap = {};
  private _referenceGroupList: HceRefGroupMap = new Map<string, HceRefGroup[]>();
  private _references: HceView[] = [];
  private _contigs: HceContig[] = [];
  private _svList: drawSV[] = [];
  private _svByChr = new Map<number, drawSV[]>();
  private _svHoverByChr = new Map<number, SvHoverEntry[]>();
  private _chrNmList: string[] = [];
  private _annotations: Record<string, drawAnnotation[]> = {};
  private _hostResizeObs?: ResizeObserver;

  // scrollbar control
  private _syncingFromViewport = false;
  private _syncingFromScrollbar = false;
  private _scrollbarRaf: number | null = null;
  private _scrollEndTimer: number | null = null;
  private readonly _scrollEndDebounceMs = 80;

  private readonly _onHScroll = () => {
    this.safeRun('onHScroll', () => {
      if (this._syncingFromViewport) return;
      this.onScrollbarInteraction();
      this.syncViewportFromScrollbars();
    });
  };

  private readonly _onVScroll = () => {
    this.safeRun('onVScroll', () => {
      if (this._syncingFromViewport) return;
      this.onScrollbarInteraction();
      this.syncViewportFromScrollbars();
    });
  };

  // rebuild control
  private _isInitializing = false;
  private _isDisposed = false;
  private _initSeq = 0; // For preventing synchronization mismatch (so that asynchronous init does not conflict)

  // tooltip（HUD）
  private tooltipCtn?: PIXI.Container;
  private tooltipBg?: PIXI.Graphics;
  private tooltipTxt?: PIXI.Text;
  private _tooltipTextPool: PIXI.Text[] = [];
  private _tooltipSepPool: PIXI.Graphics[] = [];

  // tooltip (data)
  private _svTooltipMoveRaf: number | null = null;
  private _pendingSvTooltipMove:
    | { chrId: number; chr: string; screenX: number; screenY: number }
    | null = null;

  private readonly _tooltipNumberFormat = new Intl.NumberFormat();

  private readonly _svTypeRank: Record<string, number> = {
    Translocation_origin: 0,
    Insertion: 1,
    Deletion: 2,
    Inversion: 3,
    Duplication: 4,
    Translocation_destination: 5,
  };
  private readonly _svTypeDefaultRank = 999;

  // Layout adjustment
  private readonly TOOLTIP_PAD = 6;                     // Inner margin
  private readonly TOOLTIP_OFFSET = { x: 12, y: -20 };  // Offset from the cursor
  private readonly TOOLTIP_MAX_W = 640;                 // Optional maximum (adjust according to the screen)
  private readonly TOOLTIP_LINE_H = 15;                 // Fixed line spacing (fits fontSize 11)
  private readonly LINE_GAP = 2;                        // Additional line spacing offset (optional)
  private readonly BLOCK_GAP = 3;                       // Margin between blocks
  private readonly SEP_INSET = 0;                       // Line left and right inset (0 if it fits perfectly with the background)
  private readonly SEP_ALPHA = 0.6;

  // gene label
  private _svLabelPool: PIXI.Text[] = [];
  private _measureText?: PIXI.Text;
  private _ellipsisCache = new Map<string, string>();   // key: `${text}|${bucket}`

  // clickevent
  private _lastClickAt = 0;
  private readonly _dblClickMs = 300;
  private readonly _dblClickDist = 12;
  private _lastClickPos = new PIXI.Point(0, 0);
  private _singleClickTimer: number | undefined;

  private _isDragging = false;              // Dragging flag
  private _dragStartPos = new PIXI.Point(); // Screen coordinates at the start of drag
  private readonly _dragMinDist = 4;        // If moved beyond this distance (pixels), it's a 'drag'
  private _lastDragEndedAt = 0;             // The most recent drag end time
  private readonly _dragCooldownMs = 120;   // Time (ms) to suppress tap immediately after dragging

  private _resizeRaf: number | null = null;

  // err
  private _lastErrorKey?: string;
  private _lastErrorAt = 0;

  // text style
  private readonly DEFAULT_LABEL_STYLE: Partial<PIXI.TextStyle> = {
    fontFamily: 'Inter, "Hiragino Sans", "Noto Sans JP", Arial, sans-serif',
    fontSize: 11,
    fill: 0x000000,
    align: 'center',
  } as const;

  private readonly CONT_TXT_STYLE: Partial<PIXI.TextStyle> = {
    fontFamily: 'Inter, "Hiragino Sans", "Noto Sans JP", Arial, sans-serif',
    fontSize: 11,
    fill: 0x1D72A9,
    align: 'right',
  } as const;

  private readonly REF_TXT_STYLE: Partial<PIXI.TextStyle> = {
    fontFamily: 'Inter, "Hiragino Sans", "Noto Sans JP", Arial, sans-serif',
    fontSize: 11,
    fill: 0x000000,
    align: 'right',
  } as const;

  private readonly SV_TXT_STYLE: Partial<PIXI.TextStyle> = {
    fontFamily: 'Inter, "Hiragino Sans", "Noto Sans JP", Arial, sans-serif',
    fontSize: 11,
    fill: 0x9BBB59,
    align: 'right',
  } as const;

  private readonly ANNO_TXT_STYLE: Partial<PIXI.TextStyle> = {
    fontFamily: 'Inter, "Hiragino Sans", "Noto Sans JP", Arial, sans-serif',
    fontSize: 11,
    fill: 0x784646,
    align: 'right',
  } as const;

  private normType(t?: string):
    | 'Insertion'
    | 'Duplication'
    | 'Deletion'
    | 'Inversion'
    | 'Translocation'
    | 'default' {
    const s = (t ?? '').trim().toLowerCase();
    if (s.startsWith('ins') || s === 'insertion') return 'Insertion';
    if (s.startsWith('dup') || s === 'duplication') return 'Duplication';
    if (s.startsWith('del') || s === 'deletion') return 'Deletion';
    if (s.startsWith('inv') || s === 'inversion') return 'Inversion';
    if (s.startsWith('tra') || s.startsWith('trans') || s === 'translocation') return 'Translocation';
    return 'default';
  }

  constructor(
    private hceService: HceService,
    private messageModal: MessageModalService,
    private rsvService: HceResultsSummaryViewService,
  ) { }

  async ngAfterViewInit() {
    await this.safeRunAsync('afterViewInit', async () => {
      await new Promise<void>(r => requestAnimationFrame(() => r()));
      await this.initPixi();
    });
  }

  ngOnDestroy() { this.disposePixi(); }

  // Discard → Reinitialize
  public async rebuild() {
    this.viewport.scale.x = this.minScale;
    this.viewport.position.set(0, 0);

    await this.disposePixi();
    await this.initPixi();
  }

  // PIXI Initialization (ngOnInit and Sharing)
  private async initPixi() {
    return this.safeRunAsync('initPixi', async () => {
      if (this._isInitializing) return;
      this._isInitializing = true;
      this._isDisposed = false;
      const myInitId = ++this._initSeq;

      await new Promise<void>(r => requestAnimationFrame(() => r()));
      const initWithTimeout = <T>(p: Promise<T>, ms = 4000) =>
        new Promise<T>((resolve, reject) => {
          const t = setTimeout(() => reject(new Error('PIXI init timeout')), ms);
          p.then(v => { clearTimeout(t); resolve(v); }).catch(e => { clearTimeout(t); reject(e); });
        });

      const { w: rawW, h: rawH } = await this.waitForStableHostSize();
      const width = Math.max(1, Math.floor(rawW));
      const height = Math.max(1, Math.floor(rawH));
      this.app = new PIXI.Application();
      await initWithTimeout(this.app.init({
        antialias: true,
        autoDensity: true,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        backgroundAlpha: 1,
        backgroundColor: 0xFFFFFF,
        width,
        height,
      }));
      try { this.hostRef.nativeElement.innerHTML = ''; } catch { }
      if (this.app) this.hostRef.nativeElement.appendChild((this.app?.renderer as any)?.canvas ?? (this.app as any).canvas);
      if (this._isDisposed || myInitId !== this._initSeq) {
        this._isInitializing = false;
        return;
      }

      this.layers = {
        contigs: new PIXI.Container(),
        references: new PIXI.Container(),
        svs: new PIXI.Container(),
        annotations: new PIXI.Container(),
        events: new PIXI.Container(),
      };

      this.hudSVLabels = new PIXI.Container();
      this.hudFollow = new PIXI.Container();
      this.hudFixed = new PIXI.Container();
      this.hudRuler = new PIXI.Container();
      this.hudcomments = new PIXI.Container();
      this.hudTooltip = new PIXI.Container();

      // reference HUD
      this.hudReferenceFramesG = new PIXI.Graphics();
      this.hudFollow.addChild(this.hudReferenceFramesG);

      this._svLabelPool = [];
      this._tooltipTextPool = [];
      this._tooltipSepPool = [];
      this._measureText = undefined;
      this._ellipsisCache.clear();

      // ===== Viewport =====
      this.viewportRoot = new PIXI.Container();
      this.viewportRoot.position.set(LAYOUT.label, LAYOUT.ruler);
      this.app.stage.addChild(this.viewportRoot);

      this.viewport = new Viewport({
        events: this.app.renderer.events,
        ticker: this.app.ticker,
        disableOnContextMenu: true,
        screenWidth: this.getViewportScreenWidth(),
        screenHeight: this.getViewportScreenHeight(),
        worldWidth: 250_000 * this.baseRate,
        worldHeight: 200,
        passiveWheel: false,
      })
        .drag()
        .wheel({ smooth: 1, percent: 1, interrupt: true })
        .pinch({ noDrag: false })
        .clampZoom({ minScale: this.minScale, maxScale: this.maxScale })
        .clamp({ direction: 'all', underflow: 'top-left' });

      this.viewportRoot.addChild(this.viewport);
      this.viewport.scale.y = 1;

      this.worldMask = new PIXI.Graphics()
        .rect(0, 0, this.getViewportScreenWidth(), this.getViewportScreenHeight())
        .fill(0x000000);
      this.viewportRoot.addChild(this.worldMask);
      this.viewport.mask = this.worldMask;

      // ===== HUD Layer =====
      this.app.stage.addChild(this.hudSVLabels);
      this.app.stage.addChild(this.hudFollow);
      this.app.stage.addChild(this.hudFixed);
      this.app.stage.addChild(this.hudRuler);
      this.app.stage.addChild(this.hudcomments);
      this.app.stage.addChild(this.hudTooltip);
      this.hudSVLabels.sortableChildren = true;
      this.hudFollow.sortableChildren = true;
      this.hudRuler.sortableChildren = true;
      this.hudFixed.sortableChildren = true;
      this.hudcomments.sortableChildren = true;
      this.hudTooltip.sortableChildren = true;

      // ===== World Layers =====
      this.viewport.addChild(this.layers.contigs);
      this.viewport.addChild(this.layers.references);
      this.viewport.addChild(this.layers.svs);
      this.viewport.addChild(this.layers.annotations);
      this.viewport.addChild(this.layers.events);
      this.layers.contigs.sortableChildren = true;
      this.layers.references.sortableChildren = true;
      this.layers.svs.sortableChildren = true;
      this.layers.annotations.sortableChildren = true;
      this.layers.events.sortableChildren = true;

      // ===== Data Reflection =====
      this._sampleName = this.analysis?.SampleName ?? null;
      this._refsMappingTable = this.filterRefNameMap
      this._references = this.references;
      this._contigs = this.contigs;
      this._svList = this.filterSvList.map(sv => ({
        ...sv,
        visibled: true,
        highlight: false,
      }));
      this.rebuildSvIndex();
      this.rebuildSvHoverIndex();
      this._annotations = Object.fromEntries(
        Object.entries(this.annotations ?? {}).map(([key, arr]) => [
          key,
          arr.map(a => ({
            ...a,
            visibled: true,
            highlight: false,
          })),
        ])
      );
      this._chrNmList = Object.values(this.refsMappingTable) ?? [];
      this._referenceGroupList = this.rsvService.mkRefGrpList(this._references, this._chrNmList);

      // ===== Layout calculation =====
      LAYOUT.worldKb = this.rsvService.computeWorldKb(this._referenceGroupList);
      const screenPxW = this.app.renderer.width;

      this.baseRate = LAYOUT.worldKb > 0
        ? (screenPxW - LAYOUT.label - LAYOUT.marginRight) / LAYOUT.worldKb
        : 0.01;

      const worldWidthPx = this.getWorldWidthPx()
      LAYOUT.hasAnno = Object.keys(this._annotations).length;
      if (LAYOUT.hasAnno > 0) {
        // Total height = Line height * Number of lines + Gap * (Number of lines - 1)
        LAYOUT.annotation.h = this.ANNO_LINE_H * LAYOUT.hasAnno + LAYOUT.gap * Math.max(0, LAYOUT.hasAnno - 1);
        LAYOUT.marginBottom = 6;
      } else {
        LAYOUT.annotation.h = 0;
        LAYOUT.marginBottom = 0;
      }
      LAYOUT.chrH = this.computeLaneY();

      const totalH = this.getWorldHeightPx();
      this.viewport.resize(
        this.getViewportScreenWidth(),
        this.getViewportScreenHeight(),
        worldWidthPx,
        totalH
      );

      // ===== Drawing & HUD =====
      this.redraw();
      this.updateReferenceFramesHUD();
      this.buildHybridLabelsAndSeparators();
      this.redrawRulerDynamic();
      this.updateSVLabelsHUD();
      await this.addHudComments(width, this.HUD_IMG_Y);
      this.viewport?.scale.set(this.minScale, 1);
      this.viewport?.moveCorner(0, 0);

      // ===== Events =====
      this.viewport?.on('moved', () => {
        this.syncScrollbarsFromViewport();
      });

      this.viewport?.on('drag-start', () => {
        this._isDragging = true;
        this.hideUnusedLabelsFromPool(0);
        if (this._singleClickTimer != null) {
          clearTimeout(this._singleClickTimer);
          this._singleClickTimer = undefined;
        }
      });

      this.viewport?.on('drag-end', () => {
        this._isDragging = false;
        this._lastDragEndedAt = performance.now ? performance.now() : Date.now();
        this.updateSVLabelsHUD();
      });

      this.viewport?.on('zoomed', () => {
        this.viewport.scale.y = 1;
        this.refreshScrollbarMetrics();
      });

      this.viewport?.on('zoomed-end', () => {
        // Refresh lightweight HUD for zoom projection changes.
        this.refreshFollowHud({ syncScrollbar: true });
        this.updateSVLabelsHUD();
      });

      this.viewport?.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
        this._dragStartPos.set(e.global.x, e.global.y);
      });

      window.addEventListener('resize', this.onResize);
      this.initTooltip();

      this._hostResizeObs = new ResizeObserver(() => {
        this.scheduleResize();
      });

      this._hostResizeObs.observe(this.hostRef.nativeElement);

      this.bindScrollbars();
      this.refreshScrollbarMetrics();
      this.syncScrollbarsFromViewport();

      this.initialized.emit();
      this._isInitializing = false;
    });
  }

  private async waitForStableHostSize(framesStable = 2, maxFrames = 60): Promise<{ w: number; h: number }> {
    let lastW = 0, lastH = 0, stable = 0;
    for (let i = 0; i < maxFrames; i++) {
      await new Promise<void>(r => requestAnimationFrame(() => r()));
      const el = this.hostRef.nativeElement;
      const w = el.clientWidth | 0;
      const h = el.clientHeight | 0;
      if (w > 0 && h > 0 && w === lastW && h === lastH) {
        if (++stable >= framesStable) return { w, h };
      } else {
        stable = 0; lastW = w; lastH = h;
      }
    }
    return { w: Math.max(1, lastW), h: Math.max(1, lastH) };
  }

  // Complete disposal of PIXI (ngOnDestroy and shared)
  private disposePixi() {
    this._isDisposed = true;
    this.cancelSvTooltipMove();

    if (this._scrollEndTimer != null) {
      clearTimeout(this._scrollEndTimer);
      this._scrollEndTimer = null;
    }

    try { window.removeEventListener('resize', this.onResize); } catch { }

    this.unbindScrollbars();

    try {
      this._hostResizeObs?.disconnect();
    } catch { }
    this._hostResizeObs = undefined;
    
    if (this._resizeRaf != null) {
      cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = null;
    }

    if (this._scrollbarRaf != null) {
      cancelAnimationFrame(this._scrollbarRaf);
      this._scrollbarRaf = null;
    }

    // Discard HUD/Layers (including children)
    try {
      this.worldMask?.destroy();
      this.viewportRoot?.destroy({ children: true });
      this.hudSVLabels?.destroy({ children: true });
      this.hudFollow?.destroy({ children: true });
      this.hudFixed?.destroy({ children: true });
      this.hudRuler?.destroy({ children: true });
      this.hudcomments?.destroy({ children: true });
      this.hudTooltip?.destroy({ children: true });

      this.layers?.contigs?.destroy({ children: true });
      this.layers?.references?.destroy({ children: true });
      this.layers?.svs?.destroy({ children: true });
      this.layers?.annotations?.destroy({ children: true });
      this.layers?.events?.destroy({ children: true });
    } catch { }

    // Destroy the pool
    try {
      for (const t of this._svLabelPool) t?.destroy();
      for (const t of this._tooltipTextPool) t?.destroy();
      for (const g of this._tooltipSepPool) g?.destroy();
      this._svLabelPool = [];
      this._tooltipTextPool = [];
      this._tooltipSepPool = [];
      this._measureText?.destroy();
      this._measureText = undefined;
      this._ellipsisCache.clear();
    } catch { }

    try { this.viewport?.destroy(); } catch { }
    try { this.app?.destroy(true); } catch { }
    try { if (this.hostRef?.nativeElement) this.hostRef.nativeElement.innerHTML = ''; } catch { }

    this._isInitializing = false;
    (this as any).app = undefined;
    (this as any).viewport = undefined;
  }

  private scheduleResize = () => {
    if (this._resizeRaf != null) return;

    this._resizeRaf = requestAnimationFrame(() => {
      this._resizeRaf = null;
      if (!this.app || (this.app as any).destroyed || !this.viewport) return;
      if (!this.hostRef?.nativeElement) return;

      const rect = this.hostRef.nativeElement.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));

      const oldBaseRate = this.baseRate;

      if (LAYOUT.worldKb > 0) {
        this.baseRate = Math.max(
          0.000001,
          (w - LAYOUT.label - LAYOUT.marginRight) / LAYOUT.worldKb
        );
      }

      const worldWidthPx = this.getWorldWidthPx();
      const worldHeightPx = this.getWorldHeightPx();

      this.app.renderer.resize(w, h);

      this.viewportRoot.position.set(LAYOUT.label, LAYOUT.ruler);

      this.viewport.resize(
        this.getViewportScreenWidth(),
        this.getViewportScreenHeight(),
        worldWidthPx,
        worldHeightPx
      );

      this.viewport.scale.y = 1;

      this.worldMask?.clear()
        .rect(0, 0, this.getViewportScreenWidth(), this.getViewportScreenHeight())
        .fill(0x000000);

      // If the baseRate has changed, also recreate the coordinates of the Graphics.
      if (Math.abs(oldBaseRate - this.baseRate) > 1e-12) {
        this.redraw();
      }

      this.viewport.scale.set(this.minScale, 1);
      this.viewport.moveCorner(0, 0);

      this.updateReferenceFramesHUD();
      this.buildHybridLabelsAndSeparators();
      this.updateHybridLabelsAndSeparatorsPosition?.();
      this.redrawRulerDynamic();
      this.updateSVLabelsHUD();
      this.addHudComments(w, this.HUD_IMG_Y);

      this.refreshScrollbarMetrics();
      this.syncScrollbarsFromViewport();
    });
  };

  private onResize = () => {
    this.safeRun('onResize', () => {
      if (!this.app || (this.app as any).destroyed || !this.viewport) return;
      if (!this.hostRef?.nativeElement) return;

      const w = Math.max(1, Math.floor(this.hostRef.nativeElement.clientWidth));
      const h = Math.max(1, Math.floor(this.hostRef.nativeElement.clientHeight));
      if (LAYOUT.worldKb > 0) {
        this.baseRate = (w - LAYOUT.label - LAYOUT.marginRight) / LAYOUT.worldKb;
      }

      const worldWidthPx = this.getWorldWidthPx();
      this.app?.renderer.resize(w, h);
      this.viewportRoot?.position.set(LAYOUT.label, LAYOUT.ruler);
      this.viewport?.resize(
        this.getViewportScreenWidth(),
        this.getViewportScreenHeight(),
        worldWidthPx,
        this.getWorldHeightPx()
      );
      this.viewport.scale.y = 1;
      this.worldMask?.clear()
        .rect(0, 0, this.getViewportScreenWidth(), this.getViewportScreenHeight())
        .fill(0x000000);

      if (this._chrNmList.length) {
        this.safeRun('updateReferenceFramesHUD', () => this.updateReferenceFramesHUD());
        this.safeRun('buildHybridLabelsAndSeparators', () => this.buildHybridLabelsAndSeparators());
      }

      this.safeRun('redrawRulerDynamic', () => this.redrawRulerDynamic());
      this.safeRun('updateSVLabelsHUD', () => this.updateSVLabelsHUD());
      this.safeRunAsync('addHudComments', () => this.addHudComments(w, this.HUD_IMG_Y));
      this.safeRun('refreshScrollbarMetrics', () => this.refreshScrollbarMetrics());
      this.safeRun('syncScrollbarsFromViewport', () => this.syncScrollbarsFromViewport());
      this.redraw();
    });
  };

  private initScale(): void {
    this.hideTooltip();
    this.viewport.moveCorner(0, 0);
    this.viewport.scale.x = this.minScale;
    this.viewport.scale.y = 1;
    this.refreshScrollbarMetrics();
    this.refreshFollowHud({ syncScrollbar: true });
    this.updateSVLabelsHUD();
  }

  // Apply hiding of shared SV, single selection, and duplication rate all at once
  public applyVisibilityOptions(opts: {
    hideSharedSv?: boolean;
    overlapRate?: number;
    selectedSample?: string | null;
  }): void {
    if (typeof opts.hideSharedSv === 'boolean') this._hideSharedSv = opts.hideSharedSv;
    if (typeof opts.selectedSample !== 'undefined') this._selectedSample = opts.selectedSample;
    this.redraw();
  }

  // Receive and reflect the visible/highlight results from the parent
  public applyVisibilitySnapshot(v: {
    svVisible: Set<number>;
    svHighlight: Set<number>;
    annoVisible: Map<string, Set<number>>;
    annoHighlight: Map<string, Set<number>>;
    selectedSample: string | null;
    hideSharedSv: boolean,
  }): void {
    if (!v) return;
    this._hideSharedSv = !!v.hideSharedSv;

    // Reflected in SV (assuming the index is assigned by the parent as __index__)
    this._svList = this._svList.map((sv, i) => ({
      ...sv,
      visibled: v.svVisible.has(i),
      highlight: v.svHighlight.has(i),
    }));
    this.rebuildSvIndex();
    this.rebuildSvHoverIndex();

    // Reflected in Annotations
    const nextAnno: Record<string, any[]> = {};
    for (const [file, arr] of Object.entries(this._annotations ?? {})) {
      const vis = v.annoVisible.get(file) ?? new Set<number>();
      const hl = v.annoHighlight.get(file) ?? new Set<number>();
      nextAnno[file] = (arr ?? []).map((a, i) => ({
        ...a,
        visibled: vis.has(i),
        highlight: hl.has(i),
      }));
    }
    this._annotations = nextAnno;
    this._selectedSample = v.selectedSample;
    this.redraw();
  }

  public setHideSharedSv(hideSharedSv: boolean) {
    this._hideSharedSv = hideSharedSv;
    this.redraw();
  }

  public selecteSample(selectedSample: string | null) {
    this._selectedSample = selectedSample;
    this.redraw();
  }

  public onClearClick() {
    this._hideSharedSv = false;
    this._selectedSample = null;
    this.redraw();
  }

  /* ========= Drawing (World Layer) ========= */
  private redraw() {
    this.safeRun('redraw', () => {
      this.viewport.scale.y = 1; // zoom only height

      this.clearContainer(this.layers.contigs);
      this.layers.references.mask = null;
      this.clearContainer(this.layers.references);
      this.clearContainer(this.layers.svs);
      this.clearContainer(this.layers.annotations);
      this.clearContainer(this.layers.events);

      Object.entries(this._refsMappingTable).forEach(([chr_id, chr], index) => {
        this.drawContigs(parseInt(chr_id), chr, index);
        this.drawReferences(parseInt(chr_id), chr, index);
        this.drawSV(parseInt(chr_id), index);
        this.drawAnnotations(parseInt(chr_id), chr, index);
        this.drawOtherEvent(parseInt(chr_id), chr, index);
        this.drawSVEvent(parseInt(chr_id), chr, index);
        this.drawAnnotationEvent(parseInt(chr_id), chr, index);
      });
    });
  }

  private drawContigs(chr_id: number, chr: string, index: number) {
    const g = new PIXI.Graphics();
    const offsetX = 0;
    const offsetY = LAYOUT.chrH * index;
    const Y = LAYOUT.contigs.y + offsetY;
    const H = LAYOUT.contigs.h;

    this._contigs.forEach((s) => {
      if (s.Chromosome == String(chr_id)) {
        g.rect(this.kbToX(s.Start) + offsetX, Y, this.kbToX(s.End - s.Start), H)
          .fill(0x1D72A9);
      }
    });
    this.layers.contigs.addChild(g);
  }

  private drawReferences(chr_id: number, chr: string, index: number) {
    const g = new PIXI.Graphics();
    const offsetX = 0;
    const offsetY = LAYOUT.chrH * index;
    const Y = LAYOUT.references.y + offsetY;
    const H = LAYOUT.references.h;

    this._references.forEach((b) => {
      if (b.title == chr) {
        const startKb = parseInt(b.start) / 1000; // bp→kb
        const endKb = parseFloat(b.end) / 1000;
        const col = this.normalizeGieStainToPixiColor(String(b.gieStain ?? ""));
        g.rect(this.kbToX(startKb) + offsetX, Y, this.kbToX(endKb - startKb), H).fill(col ?? "0xFFFFFF");
      }
    });

    this.layers.references.addChild(g);
  }

  private drawSV(chr_id: number, index: number) {
    const g = new PIXI.Graphics();
    const offsetX = 0;
    const offsetY = LAYOUT.chrH * index;
    const Y = LAYOUT.sv.y + offsetY;
    const H = LAYOUT.sv.h;
    const selected_sample = this._selectedSample == this._sampleName;
    const width_epsilon = 0.001; // If the width is narrower than this value, draw it as a line

    g.alpha = 0.6;

    this._svList.forEach((sv) => {
      const t = this.normType(sv?.type);
      if (sv.chr_id == chr_id) {
        const color = this.rsvService.rgbToPixiColor(SV_COLORS[t] ?? SV_COLORS['default']) ?? 0;
        const visibled = sv.visibled || t == "Translocation";
        const highlight = sv.highlight;
        const strokeColor = selected_sample && highlight ? 0xFF0000 : color;
        const strokeAlpha = selected_sample && highlight ? 1 : 0.6;
        const start = this.rsvService.edgeKb(sv?.region?.chr?.start as EdgeVal | undefined, 'start');
        const end = this.rsvService.edgeKb(sv?.region?.chr?.end as EdgeVal | undefined, 'end');
        const SV_ConSize = sv?.region?.contig?.length as number;

        if (!this._hideSharedSv || visibled) {
          const x = this.kbToX(start) + offsetX;
          const e = this.kbToX(end) + offsetX;
          const conSize = this.kbToX(SV_ConSize);
          const mdw = conSize / 2;
          const c = (x + e) / 2;

          if (Math.abs(e - x) > width_epsilon || Math.abs(conSize) > width_epsilon) {
            g.moveTo(x, Y)            // Top left (top edge, inside)
              .lineTo(c - mdw, Y + H) // Bottom left (bottom edge, inside)
              .lineTo(c + mdw, Y + H) // Bottom right (bottom edge, inside)
              .lineTo(e, Y)           // Top right (top edge, inside)
              .closePath()
              .stroke({
                color: strokeColor,
                pixelLine: true,
                alpha: strokeAlpha
              })
              .fill(color);
          } else {
            // Draw as a line If the width is extremely small.
            g.moveTo(x, Y).lineTo(x, Y + H).stroke({ color: color, pixelLine: true, alpha: 0.6 });
          }
        }
      }

      // Translocation Destination
      if (t == "Translocation" && sv.chr_to_id == chr_id) {
        const t = this.normType(sv?.type);
        const color2 = this.rsvService.rgbToPixiColor(SV_COLORS[t] ?? SV_COLORS['default']) ?? 0;
        const start2 = this.rsvService.edgeKb(sv?.segment_to?.start as EdgeVal | undefined, 'start');
        const end2 = this.rsvService.edgeKb(sv?.segment_to?.end as EdgeVal | undefined, 'end');
        const x2 = this.kbToX(start2) + offsetX;
        const e2 = this.kbToX(end2) + offsetX;
        const c2 = (x2 + e2) / 2;

        if (Math.abs(e2 - x2) > width_epsilon) {
          g.moveTo(x2, Y)
            .lineTo(c2, Y + H)
            .lineTo(e2, Y)
            .closePath()
            .stroke({
              color: color2,
              pixelLine: true,
              alpha: 0.6
            })
            .fill(color2);
        } else {
          // Draw as a line If the width is extremely small.
          g.moveTo(x2, Y).lineTo(x2, Y + H).stroke({ color: color2, pixelLine: true, alpha: 0.6 });
        }
      }
    });

    this.layers.svs.addChild(g);
  }

  private drawAnnotations(chr_id: number, chr: string, index: number) {
    const g = new PIXI.Graphics();
    const offsetX = 0;
    const offsetY = LAYOUT.chrH * index;
    const files = Object.keys(this._annotations).sort();
    g.alpha = 0.5;

    files.forEach((filename, fileIdx) => {
      const annotations = this._annotations[filename] ?? [];
      const laneY = LAYOUT.annotation.y + offsetY + (this.ANNO_LINE_H + LAYOUT.gap) * fileIdx;
      const laneH = this.ANNO_LINE_H;
      const selected_sample = this._selectedSample == filename;

      for (const annotation of annotations) {
        if (annotation.chromosome === chr) {
          const color = annotation.color;
          const visibled = annotation.visibled;
          const highlight = annotation.highlight;
          const strokeColor = selected_sample && highlight ? 0xFF0000 : color;
          const strokeAlpha = selected_sample && highlight ? 1 : 0.5;
          const start = annotation.start / 1000;
          const end = annotation.end / 1000;
          const SV_ConSize = annotation.sVConSize ? annotation.sVConSize / 1000 : 0;

          if (!this._hideSharedSv || visibled) {
            const x = this.kbToX(start) + offsetX;
            const e = this.kbToX(end) + offsetX;
            const conSize = this.kbToX(SV_ConSize);
            const mdw = conSize / 2;
            const c = (x + e) / 2;

            g.moveTo(x, laneY)                // Top left (top edge, inside)
              .lineTo(c - mdw, laneY + laneH) // Bottom left (bottom edge, inside)
              .lineTo(c + mdw, laneY + laneH) // Bottom right (bottom edge, inside)
              .lineTo(e, laneY)               // Top right (top edge, inside)
              .closePath()
              .stroke({
                color: strokeColor,
                pixelLine: true,
                alpha: strokeAlpha
              })
              .fill(color);
          }
        }
      }
    });

    this.layers.annotations.addChild(g);
  }

  private drawSVEvent(chr_id: number, chr: string, index: number) {
    const g = new PIXI.Graphics();
    const offsetX = 0;
    const offsetY = LAYOUT.chrH * index;
    const Y = LAYOUT.sv.y + offsetY;
    const H = LAYOUT.sv.h;
    const end = this._referenceGroupList.get(chr)?.at(-1)!.end ?? 0;
    const endKb = end / 1000;

    g.cursor = 'pointer';
    g.eventMode = 'static';
    g.alpha = 0;
    g.rect(offsetX, Y, this.kbToX(endKb), H).fill(0x000000);

    // === Tooltip Events ===
    // move: follows the cursor (screen coordinate-based)
    g.on('pointermove', (e: PIXI.FederatedPointerEvent) => {
      this.scheduleSvTooltipMove(chr_id, chr, e.global.x, e.global.y);
    });

    // out: invisible
    g.on('pointerout', (_e: PIXI.FederatedPointerEvent) => {
      this.cancelSvTooltipMove();
      this.hideTooltip();
    });

    g.on('pointertap', (e: PIXI.FederatedPointerEvent) => {
      this.clickEvent(e, chr);
    });

    this.layers.events.addChild(g);
  }

  private drawAnnotationEvent(chr_id: number, chr: string, index: number) {
    const offsetX = 0;
    const offsetY = LAYOUT.chrH * index;
    const end = this._referenceGroupList.get(chr)?.at(-1)!.end ?? 0;
    const endX = this.kbToX(end / 1000);

    const files = Object.keys(this._annotations).sort();

    files.forEach((filename, fileIdx) => {
      const g = new PIXI.Graphics();
      const annotations = this._annotations[filename] ?? [];
      const laneY = LAYOUT.annotation.y + offsetY + (this.ANNO_LINE_H + LAYOUT.gap) * fileIdx;
      const laneH = this.ANNO_LINE_H;

      g.alpha = 0;
      g.cursor = 'pointer';
      g.eventMode = 'static';
      g.rect(offsetX, laneY, endX, laneH).fill(0xFF0000);

      g.on('pointermove', (e: PIXI.FederatedPointerEvent) => {
        const bp = this.screenXToBp(e.global.x);
        const bpKb = bp / 1000;

        const nf = new Intl.NumberFormat();
        const lines = [chr + ` \/ ${this.screenXToBp(e.global.x).toLocaleString()}bp` + `\n${filename}`];

        const buildCommonParts = (opts: {
          svid?: string | null | undefined;
          typeLabel?: string | null | undefined;  // Display like "(TYPE)"
          atBp?: number;                          // Position (kb)
          svLenBp?: number | null | undefined;    // SVLen (unit: bp)
          refLenBp?: number | null | undefined;   // RefLen (unit: bp)
          conLenBp?: number | null | undefined;   // ConLen (unit: bp)
          quality?: string | null | undefined;
          reason?: string | null | undefined;
        }) => {
          const parts: string[] = [];
          if (opts.svid && opts.typeLabel && typeof opts.atBp === 'number') {
            parts.push(`SVID: ${opts.svid} ${opts.typeLabel} @${nf.format(opts.atBp | 0)}bp`)
          }
          if (opts.svLenBp != null) parts.push(`SVLen: ${nf.format(opts.svLenBp)}bp`);
          if (opts.refLenBp != null) parts.push(`RefLen: ${nf.format(opts.refLenBp)}bp`);
          if (opts.conLenBp != null) parts.push(`ConLen: ${nf.format(opts.conLenBp)}bp`);
          if (opts.quality != null) parts.push(opts.quality);
          if (opts.reason != null) parts.push(`Reason: ${opts.reason}`);
          return parts.join(" / ");
        };

        annotations.forEach((annotation, index) => {
          const SV_ConSize = annotation.sVConSize ?? 0;
          const mdw = SV_ConSize / 2;
          const c = (annotation.start + annotation.end) / 2;
          if (
            annotation.chromosome == chr &&
            Math.min(annotation.start, c - mdw) <= bp &&
            Math.max(annotation.end, c + mdw) >= bp
          ) {
            lines.push(buildCommonParts({
              svid: annotation.svId,
              typeLabel: `(${annotation.type})`,
              atBp: annotation.start,
              svLenBp: annotation.length ?? null,
              refLenBp: annotation.sVRefSize ?? null,
              conLenBp: annotation.sVConSize ?? null,
              quality: "Quality score:",
              reason: "",
            }));
          }
        });
        this.showTooltip(lines);
        this.moveTooltip(e.global.x, e.global.y);
      });

      // out: invisible
      g.on('pointerout', (_e: PIXI.FederatedPointerEvent) => {
        this.hideTooltip();
      });

      g.on('pointertap', (e: PIXI.FederatedPointerEvent) => {
        this.clickEvent(e, chr);
      });

      this.layers.events.addChild(g);
    });
  }

  private drawOtherEvent(chr_id: number, chr: string, index: number) {
    const offsetX = 0;
    const offsetY = LAYOUT.chrH * index;
    const end = this._referenceGroupList.get(chr)?.at(-1)!.end ?? 0;
    const endX = this.kbToX(end / 1000);

    // === contig ===
    const contig = new PIXI.Graphics();
    const contigY = LAYOUT.contigs.y + offsetY;
    const contigH = LAYOUT.contigs.h + LAYOUT.gap;
    contig.cursor = 'pointer';
    contig.eventMode = 'static';
    contig.alpha = 0;
    contig.rect(offsetX, contigY, endX, contigH).fill(0x000000);

    contig.on('pointermove', (e: PIXI.FederatedPointerEvent) => {
      const info = chr + ` \/ ${this.screenXToBp(e.global.x).toLocaleString()}bp` + `\n${this._sampleName}`;
      this.showTooltip([info]);
      this.moveTooltip(e.global.x, e.global.y);
    });

    contig.on('pointerout', (_e: PIXI.FederatedPointerEvent) => {
      this.hideTooltip();
    });

    contig.on('pointertap', (e: PIXI.FederatedPointerEvent) => {
      this.clickEvent(e, chr);
    });

    // === reference ==
    const reference = new PIXI.Graphics();
    const referenceY = LAYOUT.references.y + offsetY;
    const referenceH = LAYOUT.references.h + LAYOUT.gap;
    reference.alpha = 0;
    reference.cursor = 'pointer';
    reference.eventMode = 'static';
    reference.rect(offsetX, referenceY, endX, referenceH).fill(0x000000);

    reference.on('pointermove', (e: PIXI.FederatedPointerEvent) => {
      const bp = this.screenXToBp(e.global.x);
      const label = this._references.find(b => parseFloat(b.end) >= bp && b.title == chr)?.name;
      const info = chr + ` \/ ${bp.toLocaleString()}bp` + ` \/ ${label}`;
      this.showTooltip([info]);
      this.moveTooltip(e.global.x, e.global.y);
    });

    reference.on('pointerout', (_e: PIXI.FederatedPointerEvent) => {
      this.hideTooltip();
    });

    reference.on('pointertap', (e: PIXI.FederatedPointerEvent) => {
      this.clickEvent(e, chr);
    });

    //=== Range below sv ===
    const bottom = new PIXI.Graphics();
    bottom.alpha = 0;
    bottom.cursor = 'pointer';
    bottom.eventMode = 'static';
    if (index !== 0) {
      // marginTop
      bottom.rect(offsetX, offsetY, endX, LAYOUT.marginTop).fill(0xFF0000);
    }
    bottom.rect(offsetX, LAYOUT.sv.y + LAYOUT.sv.h + offsetY, endX, LAYOUT.gap).fill(0xFF0000);

    for (let i = 0; i < LAYOUT.hasAnno; i++) {
      const laneY = LAYOUT.annotation.y + offsetY + (this.ANNO_LINE_H + LAYOUT.gap) * i;
      bottom.rect(offsetX, laneY + this.ANNO_LINE_H, endX, LAYOUT.marginBottom).fill(0xFF0000);
    }

    bottom.on('pointermove', (e: PIXI.FederatedPointerEvent) => {
      const bp = this.screenXToBp(e.global.x);
      const info = chr + ` \/ ${bp.toLocaleString()}bp`;
      this.showTooltip([info]);
      this.moveTooltip(e.global.x, e.global.y);
    });

    bottom.on('pointerout', (_e: PIXI.FederatedPointerEvent) => {
      this.hideTooltip();
    });

    bottom.on('pointertap', (e: PIXI.FederatedPointerEvent) => {
      this.clickEvent(e, chr);
    });

    //=== Outside drawing shape ===
    const other = new PIXI.Graphics();
    other.alpha = 0;
    other.cursor = 'pointer';
    other.eventMode = 'static';
    other.rect(offsetX + endX, offsetY, this.kbToX(LAYOUT.worldKb), LAYOUT.chrH).fill(0xFF0000);
    other.on('pointertap', (e: PIXI.FederatedPointerEvent) => {
      this.clickEvent(e, chr, true);
    });

    this.layers.events.addChild(contig);
    this.layers.events.addChild(reference);
    this.layers.events.addChild(bottom);
    this.layers.events.addChild(other);
  }

  /* ========= HUD ========= */
  // To follow zooming/panning while maintaining a 1px line on the HUD (screen space)
  // Re-project to screen coordinates with toScreen() each time and redraw
  private updateReferenceFramesHUD() {
    const g = this.hudReferenceFramesG;
    if (!g || !this.viewport) return;

    // Reuse a single Graphics instance to avoid frequent allocations.
    g.clear();

    const offsetX = 0;

    Object.entries(this._refsMappingTable).forEach(([chr_id, chr], index) => {
      const offsetY = LAYOUT.chrH * index;
      const Y = LAYOUT.references.y + offsetY;
      const H = LAYOUT.references.h;
      const refgroups = this._referenceGroupList.get(chr);

      refgroups?.forEach((refgroup) => {
        const start = refgroup.start;
        const end = refgroup.end;

        // Project world coordinates to screen coordinates.
        const topLeft = this.viewport.toScreen(this.kbToX(start / 1000) + offsetX, Y);
        const bottomRight = this.viewport.toScreen(this.kbToX(end / 1000) + offsetX, Y + H);
        if (!topLeft || !bottomRight) return;

        const x = Math.min(topLeft.x, bottomRight.x);
        const y = Math.min(topLeft.y, bottomRight.y);
        const w = Math.abs(bottomRight.x - topLeft.x);
        const h = Math.abs(bottomRight.y - topLeft.y);

        const R_TIP_PX = Math.min(2, Math.floor(H * 0.2));
        g.roundRect(x, y, w, h, R_TIP_PX)
          .stroke({ color: 0x000000, width: 1, alpha: 1 });
      });
    });
  }

  private buildHybridLabelsAndSeparators() {
    this.clearContainer(this.hudFixed);
    this.hudLabels.length = 0;
    this.hudSeps.length = 0;
    this.hudAnnoFileLabels.length = 0;

    const LEFT_PAD = 3;
    const RIGHT_PAD = 3;

    // The white band on the left side (background for screen fixation)
    const leftBand = new PIXI.Graphics();
    leftBand
      .rect(0, 0, LAYOUT.label - 0.5, this.app.renderer.height)
      .fill({ color: 0xFFFFFF, alpha: 1.0 });
    this.hudFixed.addChild(leftBand);

    Object.entries(this._refsMappingTable).forEach(([chr_id, chr], index) => {
      const offsetY = LAYOUT.chrH * index;

      // contig_label
      const contigYChr = LAYOUT.contigs.y + offsetY + (LAYOUT.contigs.h / 2);
      const contigTxt = this.createHudEllipsizedText({
        fullText: this._sampleName ?? "",
        worldY: contigYChr,
        style: this.CONT_TXT_STYLE,
        leftPad: LEFT_PAD,
        rightPad: RIGHT_PAD,
        cursor: 'help',
      });
      if (contigTxt) {
        this.hudLabels.push({ node: contigTxt, worldY: contigYChr });
      }

      // reference_label
      const referenceYChr = LAYOUT.references.y + offsetY + (LAYOUT.references.h / 2);
      const referenceTxt = this.createHudEllipsizedText({
        fullText: chr,
        worldY: referenceYChr,
        style: this.REF_TXT_STYLE,
        leftPad: LEFT_PAD,
        rightPad: RIGHT_PAD,
        cursor: 'help',
      });
      if (referenceTxt) {
        this.hudLabels.push({ node: referenceTxt, worldY: referenceYChr });
      }

      // sv_label
      const svYChr = LAYOUT.sv.y + offsetY + (LAYOUT.sv.h / 2);
      const svTxt = this.createHudEllipsizedText({
        fullText: this._sampleName ?? "",
        worldY: svYChr,
        style: this.SV_TXT_STYLE,
        leftPad: LEFT_PAD,
        rightPad: RIGHT_PAD,
        cursor: 'help',
      });
      if (svTxt) {
        this.hudLabels.push({ node: svTxt, worldY: svYChr });
      }

      // Annotation File Name Label (Optional + Tooltip) 
      const files = Object.keys(this._annotations).sort();
      files.forEach((filename, fileIdx) => {
        const worldYFile =
          LAYOUT.annotation.y + offsetY
          + (this.ANNO_LINE_H + LAYOUT.gap) * fileIdx
          + (this.ANNO_LINE_H / 2);

        const fileTxt = this.createHudEllipsizedText({
          fullText: filename,
          worldY: worldYFile,
          style: this.ANNO_TXT_STYLE,
          leftPad: LEFT_PAD,
          rightPad: RIGHT_PAD,
          cursor: 'help',
        });
        if (fileTxt) {
          this.hudAnnoFileLabels.push({ node: fileTxt, worldY: worldYFile, filename });
        }
      });

      // Divider line (follows the full height of the annotation)
      const screenW = (this.app.screen as any).width ?? this.app.renderer.width;
      const worldYSep =
        (LAYOUT.annotation.y + LAYOUT.annotation.h)
        + offsetY
        + LAYOUT.marginBottom;
      
      const hr = new PIXI.Graphics();
      hr.moveTo(LEFT_PAD, 0)
        .lineTo(screenW - RIGHT_PAD, 0)
        .stroke({ color: 0x949593, width: 1, alpha: 1 });
      hr.position.set(0, this.crispScreenYFromWorldY(worldYSep, 1));

      this.hudFixed.addChild(hr);
      this.hudSeps.push({ node: hr, worldY: worldYSep });
    });
  }

  private redrawRulerDynamic() {
    this.clearContainer(this.hudRuler);

    const screenW = (this.app.screen as any).width ?? this.app.renderer.width;

    // 1) Get visible range kb
    const worldLeftX = this.viewport.left;
    const worldRightX = this.viewport.right;
    const totalMaxBp = this.rsvService.getMaxBpFromReference(this._referenceGroupList);
    const totalMaxKb = this.rsvService.ceilToUnit(totalMaxBp / 1000);

    let kbMin = Math.max(0, this.worldXToKb(worldLeftX));
    let kbMax = Math.min(totalMaxKb, this.worldXToKb(worldRightX));
    if (kbMax < kbMin) [kbMin, kbMax] = [kbMax, kbMin];

    // 2) Determine the step from the 'appearance' of the scale (1/2/5×10^n)
    const pxPerKb = this.pxPerKb();
    if (pxPerKb <= 0) return;

    const rawStepKb = this.MIN_TICK_PX / pxPerKb;
    const stepKb = Math.max(this.MIN_RULER_STEP_KB, this.decideStep(rawStepKb));

    // 3) Endpoints also snap to ticks (248→250, etc.)
    const niceMinKb = Math.max(0, Math.floor(kbMin / stepKb) * stepKb);
    const niceMaxKb = Math.min(totalMaxKb, Math.ceil(kbMax / stepKb) * stepKb);

    // 4) White belt (background)
    const band = new PIXI.Graphics();
    band.rect(0, 0, screenW, this.RULER_BAND_H)
      .fill({ color: 0xFFFFFF });
    this.hudRuler.addChild(band);

    // 5) baseline
    const baseY = this.RULER_Y;
    const x0 = Math.round(this.viewport.toScreen(this.kbToWorldX(niceMinKb), 0)!.x);
    const x1 = Math.round(this.viewport.toScreen(this.kbToWorldX(niceMaxKb), 0)!.x);

    const g = new PIXI.Graphics()
      .moveTo(x0, baseY)
      .lineTo(x1, baseY)
      .stroke({ color: this.RULER_COLOR, width: 1, alpha: 1 });

    // 6) Scale & Labels (labels on top, scale protruding up and down)
    let lastLabelX = -1e9;  // Label thinning

    for (let kb = niceMinKb; kb <= niceMaxKb + 1e-9; kb += stepKb) {
      const xi = Math.round(this.viewport.toScreen(this.kbToWorldX(kb), 0)!.x);

      // Scale line: protrudes up and down
      g.moveTo(xi, baseY - this.TICK_UP_H)
        .lineTo(xi, baseY + this.TICK_DOWN_H)
        .stroke({ color: this.TICK_COLOR, width: 1, alpha: 1 });

      // Scale lines: Skip labels that stick out above and below (do not draw labels that are too close together)
      if (xi - lastLabelX < this.MIN_LABEL_PX) continue;
      lastLabelX = xi;

      const lbl = new PIXI.Text({ text: this.fmtMbFromKb(kb, stepKb), style: this.DEFAULT_LABEL_STYLE });

      // “Raise above”: Align anchor to the bottom, place slightly above the baseline
      lbl.anchor?.set(0.5, 1.0);  // x:center, y:bottom edge
      lbl.position.set(xi, baseY - this.LABEL_ABOVE_PAD_Y);
      (lbl as any).resolution = (this.app.renderer as any).resolution ?? 1;
      lbl.roundPixels = true;

      this.hudRuler.addChild(lbl);
    }
    this.hudRuler.addChild(g);

    // 7) Extend the label's blank space (non-intrusive area)
    const label = new PIXI.Graphics();
    label.rect(0, 0, this.Label_X, this.RULER_BAND_H)
      .fill({ color: 0xFFFFFF });
    this.hudRuler.addChild(label);
  }

  private updateSVLabelsHUD() {
    const PAD = 4;
    const style = this.DEFAULT_LABEL_STYLE;

    const screenW = (this.app.screen as any).width ?? this.app.renderer.width;
    const visibleMinX = 0 - 50;
    const visibleMaxX = screenW + 50;

    let labelIdx = 0; // pool's index

    Object.entries(this._refsMappingTable).forEach(([chr_id, chr], index) => {
      const chrIdNum = Number(chr_id);
      const svs = this._svByChr.get(chrIdNum) ?? [];
      if (!svs.length) return;

      const offsetY = LAYOUT.chrH * index;
      const Y = LAYOUT.sv.y + offsetY;
      const H = LAYOUT.sv.h;

      for (const sv of svs) {
        const startKb = this.rsvService.edgeKb(sv?.region?.chr?.start as any, 'start');
        const endKb = this.rsvService.edgeKb(sv?.region?.chr?.end as any, 'end');
        const xw0 = this.kbToWorldX(startKb);
        const xw1 = this.kbToWorldX(endKb);
        const sp0 = this.viewport.toScreen(xw0, 0);
        const sp1 = this.viewport.toScreen(xw1, 0);
        if (!sp0 || !sp1) continue;

        // Skip if out of visible range (determined with just 2 calls to toScreen)
        const svMinX = Math.min(sp0.x, sp1.x);
        const svMaxX = Math.max(sp0.x, sp1.x);
        if (svMaxX < visibleMinX || svMinX > visibleMaxX) continue;

        // From here, four-point projection only if necessary
        const x = this.kbToX(startKb);
        const e = this.kbToX(endKb);

        const conLenKb = Number(sv?.region?.contig?.length) || 0;
        const conSize = this.kbToX(conLenKb);
        const mdw = conSize / 2;
        const c = (x + e) / 2;

        const pBL = this.viewport.toScreen(x, Y);
        const pBR = this.viewport.toScreen(e, Y);
        const pTL = this.viewport.toScreen(c - mdw, Y + H);
        const pTR = this.viewport.toScreen(c + mdw, Y + H);
        if (!pBL || !pBR || !pTL || !pTR) continue;

        const baseW = Math.abs(pBR.x - pBL.x);
        const topW = Math.abs(pTR.x - pTL.x);
        const maxLabelW = Math.max(0, Math.min(baseW, topW) - PAD * 2);

        // LOD that 'does not output a label in the first place' if it is below a certain threshold
        if (maxLabelW < 20) continue;

        const raw = this.rsvService.makeGeneText(sv?.region?.chr?.affected_genes ?? []);
        const shown = this.ellipsizeCached(raw, maxLabelW, style);
        if (!shown) continue;

        const midScreen = this.viewport.toScreen((x + e) / 2, Y + H / 2);
        if (!midScreen) continue;

        const txt = this.getLabelFromPool(labelIdx++, style);
        txt.text = shown;
        txt.position.set(Math.round(midScreen.x), Math.round(midScreen.y));
        txt.visible = true;
      }
    });

    // Make the unused pool invisible
    this.hideUnusedLabelsFromPool(labelIdx);
  }

  private async addHudComments(offset_x: number, offset_y: number) {
    await this.safeRunAsync('addHudComments.load', async () => {
      if (!this.hudcomments) this.hudcomments = new PIXI.Container();
      this.clearContainer(this.hudcomments);

      const images = [
        {
          url: './assets/hce/greybox.png',
          label: 'Reference Genome',
        },
        {
          url: './assets/hce/bluebox.png',
          label: 'Whole Genome Map',
        },
      ];
      let endX = offset_x;
      await PIXI.Assets.load(images.map(img => img.url));

      images.forEach((image) => {
      if (!this.app || (this.app as any).destroyed) return;
        const tex = PIXI.Texture.from(image.url);
        const sp = new PIXI.Sprite(tex);

        // Initial scale (shrink when the image is too large)
        this.fitHudImage(sp);

        // lbael
        const label = new PIXI.Text({ text: image.label, style: this.DEFAULT_LABEL_STYLE });
        endX = endX - label.width - this.HUD_IMG_GAP;
        label.position.set(endX, offset_y);
        if (this.app) (label as any).resolution = (this.app.renderer as any).resolution ?? 1;
        label.roundPixels = true;

        // image
        endX = endX - sp.width - this.HUD_IMG_GAP;
        sp.position.set(endX, offset_y);

        this.hudcomments.addChild(sp);
        this.hudcomments.addChild(label);
      });

      // Credit
      const Credit = new PIXI.Text({ text: "(Mbp)", style: this.DEFAULT_LABEL_STYLE });
      Credit.position.set(offset_x - Credit.width - this.HUD_IMG_GAP, offset_y + this.CREDIT_BELLOW_PAD_Y);
      if (this.app) (Credit as any).resolution = (this.app.renderer as any).resolution ?? 1;
      Credit.roundPixels = true;
      this.hudcomments.addChild(Credit);
    });
  }

  private refreshFollowHud(opts?: { syncScrollbar?: boolean }): void {
    // Refresh lightweight HUD elements that must follow movement.
    this.updateReferenceFramesHUD();
    this.updateHybridLabelsAndSeparatorsPosition();
    this.redrawRulerDynamic();
    this.hideTooltip();

    if (opts?.syncScrollbar) {
      this.syncScrollbarsFromViewport();
    }
  }

  private bindScrollbars(): void {
    const h = this.hScrollerRef?.nativeElement;
    const v = this.vScrollerRef?.nativeElement;

    if (h) h.addEventListener('scroll', this._onHScroll, { passive: true });
    if (v) v.addEventListener('scroll', this._onVScroll, { passive: true });
  }

  private unbindScrollbars(): void {
    const h = this.hScrollerRef?.nativeElement;
    const v = this.vScrollerRef?.nativeElement;

    if (h) h.removeEventListener('scroll', this._onHScroll);
    if (v) v.removeEventListener('scroll', this._onVScroll);
  }

  // Recalculating the scroll area
  private refreshScrollbarMetrics(): void {
    const hScroller = this.hScrollerRef?.nativeElement;
    const vScroller = this.vScrollerRef?.nativeElement;
    const hSpacer = this.hSpacerRef?.nativeElement;
    const vSpacer = this.vSpacerRef?.nativeElement;

    if (!hScroller || !vScroller || !hSpacer || !vSpacer || !this.viewport) return;

    const scaledWorldW = Math.max(
      this.getViewportScreenWidth(),
      this.getWorldWidthPx() * this.viewport.scale.x
    );

    const scaledWorldH = Math.max(
      this.getViewportScreenHeight(),
      this.getWorldHeightPx() * this.viewport.scale.y
    );

    hSpacer.style.width = `${Math.ceil(scaledWorldW)}px`;
    vSpacer.style.height = `${Math.ceil(scaledWorldH)}px`;
  }

  private onScrollbarInteraction(): void {
    // Hide heavy HUD labels while scrolling.
    this.hideUnusedLabelsFromPool(0);

    // Hide tooltip during scrollbar interaction.
    this.hideTooltip();

    if (this._scrollEndTimer != null) {
      clearTimeout(this._scrollEndTimer);
    }

    this._scrollEndTimer = window.setTimeout(() => {
      this._scrollEndTimer = null;

      if (!this.viewport || !this.app || (this.app as any).destroyed) return;

      // Redraw heavy HUD elements only after scrolling settles.
      this.updateSVLabelsHUD();
    }, this._scrollEndDebounceMs);
  }

  // Syncing the viewport area with the scrollbar.
  private syncScrollbarsFromViewport(): void {
    const hScroller = this.hScrollerRef?.nativeElement;
    const vScroller = this.vScrollerRef?.nativeElement;

    if (!hScroller || !vScroller || !this.viewport) return;
    if (this._syncingFromScrollbar) return;

    this._syncingFromViewport = true;
    try {
      const leftPx = Math.max(0, -this.viewport.position.x);
      const topPx = Math.max(0, -this.viewport.position.y);

      if (Math.abs(hScroller.scrollLeft - leftPx) > 1) {
        hScroller.scrollLeft = leftPx;
      }

      if (Math.abs(vScroller.scrollTop - topPx) > 1) {
        vScroller.scrollTop = topPx;
      }
    } finally {
      this._syncingFromViewport = false;
    }
  }

  private syncViewportFromScrollbars(): void {
    const hScroller = this.hScrollerRef?.nativeElement;
    const vScroller = this.vScrollerRef?.nativeElement;

    if (!hScroller || !vScroller || !this.viewport) return;
    if (this._syncingFromViewport) return;

    this._syncingFromScrollbar = true;
    try {
      const leftWorld = hScroller.scrollLeft / this.viewport.scale.x;
      const topWorld = vScroller.scrollTop / this.viewport.scale.y;
      this.viewport.moveCorner(leftWorld, topWorld);

      // Handle scrollbar-driven movement directly here.
      this.refreshFollowHud({ syncScrollbar: false });
    } finally {
      this._syncingFromScrollbar = false;
    }
  }

  private clearContainer(container?: PIXI.Container): void {
    if (!container) return;

    const children = container.removeChildren();
    for (const child of children) {
      child.destroy({ children: true });
    }
  }

  /* ========= tooltip ========= */
  private initTooltip() {
    this.tooltipCtn = new PIXI.Container();
    this.tooltipCtn.visible = false;
    this.tooltipCtn.zIndex = 9_999;

    // background
    this.tooltipBg = new PIXI.Graphics();
    this.tooltipCtn.addChild(this.tooltipBg);

    // text
    this.tooltipTxt = new PIXI.Text({ text: '', style: this.SV_TXT_STYLE });
    this.tooltipTxt.roundPixels = true;

    this.tooltipCtn.addChild(this.tooltipTxt);
    this.hudTooltip.addChild(this.tooltipCtn);
  }

  private showTooltip(lines: string[]) {
    if (!this.tooltipCtn || !this.tooltipBg) return;

    // The lines to be received are assumed to be "1 header line + multiple lines of text for each entry." Since entries.block contains `
    // `, split them accordingly to form "blocks." Example: ["chr15 / ...", "SVID... r(origin)... rGenes:...", "SVID..."] etc.
    const blocks = lines.map(s => s.split(/\r/));

    // layout
    const pad = this.TOOLTIP_PAD;
    const x0 = pad;
    let cursorY = pad;
    const style: Partial<PIXI.TextStyle> = {
      fontFamily: 'Inter, "Hiragino Sans", "Noto Sans JP", Arial, sans-serif',
      fontSize: 11,
      fill: 0x000000,
      lineHeight: this.TOOLTIP_LINE_H,
      wordWrap: true,
      wordWrapWidth: this.TOOLTIP_MAX_W
    };

    let textIdx = 0;
    let sepIdx = 0;
    let maxW = 0;

    const tempWidths: number[] = [];
    const tempHeights: number[] = [];
    const perLineNodes: PIXI.Text[][] = [];

    for (const block of blocks) {
      const nodes: PIXI.Text[] = [];
      let blockMaxW = 0;
      let blockH = 0;
      for (const rawLine of block) {
        const t = this.getTooltipTextFromPool(textIdx++, style);
        t.text = rawLine ?? '';
        t.position.set(x0, cursorY + blockH);
        t.visible = true;

        // Actual measurement
        const w = Math.ceil(t.width);
        const h = Math.ceil(t.height);
        blockMaxW = Math.max(blockMaxW, w);
        blockH += h + this.LINE_GAP;

        nodes.push(t);
      }
      if (block.length > 0) blockH -= this.LINE_GAP;

      perLineNodes.push(nodes);
      tempWidths.push(blockMaxW);
      tempHeights.push(blockH);

      maxW = Math.max(maxW, blockMaxW);
      cursorY += blockH + this.BLOCK_GAP;
    }

    const totalH = cursorY - this.BLOCK_GAP + pad;
    const bgW = Math.ceil(maxW) + pad * 2;
    const bgH = Math.ceil(totalH);

    let accY = pad;
    for (let bi = 0; bi < blocks.length; bi++) {
      accY += tempHeights[bi];
      if (bi < blocks.length - 1) {
        const centerY = Math.round(accY + this.BLOCK_GAP / 2) + this.snapOffset(1); // 中央
        const g = this.getTooltipSepFromPool(sepIdx++);
        g.clear();
        g.moveTo(pad + this.SEP_INSET, centerY)
          .lineTo(pad + Math.max(0, maxW - this.SEP_INSET), centerY)
          .stroke({ color: 0x000000, width: 1, alpha: this.SEP_ALPHA });
        g.visible = true;
        accY += this.BLOCK_GAP;
      }
    }

    this.tooltipBg.clear();
    this.tooltipBg.rect(0, 0, bgW, bgH)
      .fill({ color: 0xFFF3DC, alpha: 0.8 })
      .stroke({ color: 0x000000, width: 1, alpha: 0.9 });

    this.tooltipCtn.visible = true;
    this.hideUnusedTooltipNodes(textIdx, sepIdx);
  }

  private moveTooltip(screenX: number, screenY: number) {
    if (!this.tooltipCtn || !this.tooltipBg || !this.tooltipTxt) return;

    const offsetX = this.TOOLTIP_OFFSET.x;
    const offsetY = this.TOOLTIP_OFFSET.y;
    let x = screenX + offsetX;
    let y = screenY + offsetY;

    const screenW = (this.app.screen as any).width ?? this.app.renderer.width;
    const screenH = (this.app.screen as any).height ?? this.app.renderer.height;
    const w = this.tooltipBg.width;
    const h = this.tooltipBg.height;

    if (x + w > screenW - 2) x = Math.max(2, screenX - w - 2);
    if (y + h > screenH - 2) y = Math.max(2, screenH - h - 2);

    this.tooltipCtn.position.set(x, y);
  }

  private hideTooltip() {
    if (!this.tooltipCtn) return;
    this.tooltipCtn.visible = false;
  }

  private scheduleSvTooltipMove(chrId: number, chr: string, screenX: number, screenY: number): void {
    this._pendingSvTooltipMove = { chrId, chr, screenX, screenY };

    if (this._svTooltipMoveRaf != null) return;

    this._svTooltipMoveRaf = requestAnimationFrame(() => {
      this._svTooltipMoveRaf = null;

      const pending = this._pendingSvTooltipMove;
      this._pendingSvTooltipMove = null;

      if (!pending || !this.viewport || !this.app || (this.app as any).destroyed) return;

      const bp = this.screenXToBp(pending.screenX);
      const bpKb = bp / 1000;

      const lines: string[] = [
        `${pending.chr} / ${this._tooltipNumberFormat.format(bp)}bp\r${this._sampleName}`
      ];

      const entries = this._svHoverByChr.get(pending.chrId) ?? [];
      for (const entry of entries) {
        if (entry.minKb <= bpKb && bpKb <= entry.maxKb) {
          lines.push(entry.block);
        }
      }

      this.showTooltip(lines);
      this.moveTooltip(pending.screenX, pending.screenY);
    });
  }

  private cancelSvTooltipMove(): void {
    this._pendingSvTooltipMove = null;

    if (this._svTooltipMoveRaf != null) {
      cancelAnimationFrame(this._svTooltipMoveRaf);
      this._svTooltipMoveRaf = null;
    }
  }

  /* ========= utility ========= */
  // Exception → Modal focused processing (Suppressing spam of the same message: 1.5s)
  private handleError(err: unknown, context: string) {
    const msg = (err instanceof Error ? err.message : String(err || 'Unknown error')).trim();
    const key = `${context}|${msg}`;
    const now = performance.now ? performance.now() : Date.now();
    if (this._lastErrorKey === key && (now - this._lastErrorAt) < 1500) return; // 抑止

    this._lastErrorKey = key;
    this._lastErrorAt = now;
    console.log(err);
    this.messageModal.openModal($localize`Error`, $localize`An error occurred during the rendering process.`);
  }

  // Catch exceptions and execute safely (supports both with and without return value)
  private safeRun<T>(label: string, fn: () => T): T | undefined {
    try { return fn(); } catch (e) { this.handleError(e, label); return undefined; }
  }
  private async safeRunAsync<T>(label: string, fn: () => Promise<T>): Promise<T | undefined> {
    try { return await fn(); } catch (e) { this.handleError(e, label); return undefined; }
  }

  private computeLaneY(): number {
    let cursor = LAYOUT.marginTop;
    LAYOUT.contigs.y = cursor;
    cursor += LAYOUT.contigs.h + LAYOUT.gap;
    LAYOUT.references.y = cursor;
    cursor += LAYOUT.references.h + LAYOUT.gap;
    LAYOUT.sv.y = cursor;
    cursor += LAYOUT.sv.h + LAYOUT.gap;
    LAYOUT.annotation.y = cursor;
    cursor += LAYOUT.annotation.h + LAYOUT.marginBottom;
    return cursor;
  }

  private fitHudImage(sp: PIXI.Sprite) {
    const texW = sp.texture.width || 1;
    const texH = sp.texture.height || 1;
    const sx = this.HUD_IMG_MAX_W / texW;
    const sy = this.HUD_IMG_MAX_H / texH;
    const s = Math.min(1, sx, sy);
    sp.scale.set(s);
  }

  private getViewportScreenWidth(): number {
    return Math.max(1, this.app.renderer.width - LAYOUT.label);
  }

  private getViewportScreenHeight(): number {
    return Math.max(1, this.app.renderer.height - LAYOUT.ruler);
  }

  private getWorldWidthPx(): number {
    return Math.max(1, this.kbToX(LAYOUT.worldKb) + LAYOUT.marginRight);
  }

  private getWorldHeightPx(): number {
    return Math.max(1, LAYOUT.chrH * this._chrNmList.length + LAYOUT.gap);
  }

  private kbToX(kb: number) {
    return kb * this.baseRate;
  }

  // kb <-> worldX(px in world)
  private kbToWorldX(kb: number): number {
    return this.kbToX(kb);
  }

  private worldXToKb(worldX: number): number {
    return worldX / this.baseRate;
  }

  private screenXToWorldX(screenX: number): number {
    const p = this.viewport.toWorld(screenX, 0);
    return p?.x ?? 0;
  }

  private screenXToBp(screenX: number): number {
    const bp = this.worldXToKb(this.screenXToWorldX(screenX)) * 1000;
    return Math.ceil(bp);
  }

  private updateHybridLabelsAndSeparatorsPosition(): void {
    for (const l of this.hudLabels) {
      l.node.y = this.screenYFromWorldY(l.worldY);
    }

    for (const a of this.hudAnnoFileLabels) {
      a.node.y = this.screenYFromWorldY(a.worldY);
    }

    for (const s of this.hudSeps) {
      s.node.y = this.crispScreenYFromWorldY(s.worldY, 1);
    }
  }

  private screenYFromWorldY(worldY: number): number {
    const sp = this.viewport.toScreen(0, worldY);
    return sp ? Math.round(sp.y) : 0;
  }

  private crispScreenYFromWorldY(worldY: number, lineWidth = 1): number {
    const sp = this.viewport.toScreen(0, worldY);
    if (!sp) return 0;
    return Math.round(sp.y) + this.snapOffset(lineWidth);
  }

  // How many px is 1kb on the screen (with the current zoom/pan)
  private pxPerKb(): number {
    const w0 = this.kbToWorldX(0);
    const w1 = this.kbToWorldX(1);
    const s0 = this.viewport.toScreen(w0, 0);
    const s1 = this.viewport.toScreen(w1, 0);
    if (!s0 || !s1) return 0;
    return Math.abs(s1.x - s0.x);
  }

  // Round in steps of 1, 2, 5 × 10^n
  private decideStep(raw: number): number {
    if (raw <= 0) return 0;
    const exp = Math.floor(Math.log10(raw));
    const frac = raw / Math.pow(10, exp);
    let decideFrac: number;
    if (frac <= 1) decideFrac = 1;
    else if (frac <= 2) decideFrac = 2;
    else if (frac <= 5) decideFrac = 5;
    else decideFrac = 10;
    return decideFrac * Math.pow(10, exp);
  }

  private fmtMbFromKb(kb: number, stepKb: number): string {
    const mb = kb / 1000;
    const stepMb = stepKb / 1000;
    let dp = 0;
    if (stepMb < 1) {
      dp = Math.min(3, Math.max(0, Math.ceil(-Math.log10(stepMb))));
    }

    return mb
      .toFixed(dp)
      .replace(/\.0+$/, '') // 1.0   -> 1
      .replace(/(\.\d*?[1-9])0+$/, '$1'); // 1.10 -> 1.1, 1.230 -> 1.23
  }

  private snapOffset(lineWidthPx = 1): number {
    const r = (this.app.renderer as any).resolution ?? 1;
    return (lineWidthPx % 2 ? 0.5 : 0) / r;
  }

  private ellipsizeCached(text: string, maxPx: number, style: Partial<PIXI.TextStyle>): string {
    const bucket = Math.max(0, Math.floor(maxPx / 4));
    const key = `${text}|${bucket}`;
    const hit = this._ellipsisCache.get(key);
    if (hit != null) return hit;
    const out = this.ellipsizeToPx(text, maxPx, style);
    this._ellipsisCache.set(key, out);
    return out;
  }

  // Normalize gneg/gpos… type or "#RRGGBB" or "rgb()" to a 0xRRGGBB value and return it. 
  // If it does not match, return COL_REF_OTHER.
  private normalizeGieStainToPixiColor(input: string): number {
    if (!input) {
      return this.rsvService.rgbToPixiColor(GIE_STAIN_COLOR_MAP['other'])!;
    }

    const val = input.trim();

    // 1) #RRGGBB
    const hex = this.rsvService.hexToPixiColor(val);
    if (hex !== null) return hex;

    // 2) rgb(r,g,b)
    const rgb = this.rsvService.rgbToPixiColor(val);
    if (rgb !== null) return rgb;

    // 3) Type Name → Map Resolution (Case Insensitive, Whitespace Allowed)
    const key = val.toLowerCase();
    const mapped = GIE_STAIN_COLOR_MAP[key];
    if (mapped) {
      const c = this.rsvService.rgbToPixiColor(mapped) ?? this.rsvService.hexToPixiColor(mapped);
      if (c !== null) return c;
    }

    // 4) fallback
    return this.rsvService.rgbToPixiColor(GIE_STAIN_COLOR_MAP['other'])!;
  }

  private getTooltipTextFromPool(i: number, style: Partial<PIXI.TextStyle>): PIXI.Text {
    if (this._tooltipTextPool[i]) return this._tooltipTextPool[i];
    const t = new PIXI.Text({ text: '', style: style });
    t.roundPixels = true;
    (t as any).resolution = (this.app.renderer as any).resolution ?? 1;
    this.tooltipCtn!.addChild(t);
    this._tooltipTextPool[i] = t;
    return t;
  }

  private getTooltipSepFromPool(i: number): PIXI.Graphics {
    if (this._tooltipSepPool[i]) return this._tooltipSepPool[i];
    const g = new PIXI.Graphics();
    this.tooltipCtn!.addChild(g);
    this._tooltipSepPool[i] = g;
    return g;
  }

  private hideUnusedTooltipNodes(textCount: number, sepCount: number) {
    for (let i = textCount; i < this._tooltipTextPool.length; i++) {
      const t = this._tooltipTextPool[i]; if (t) t.visible = false;
    }
    for (let i = sepCount; i < this._tooltipSepPool.length; i++) {
      const g = this._tooltipSepPool[i]; if (g) g.visible = false;
    }
  }

  private ensureMeasure(style: Partial<PIXI.TextStyle>) {
    if (!this._measureText) {
      this._measureText = new PIXI.Text({ text: '', style: style });
      this._measureText.visible = false;
      this.app.stage.addChild(this._measureText);
    } else {
      this._measureText.style = new PIXI.TextStyle(style as any);
    }
  }

  private textWidthPx(s: string): number {
    this._measureText!.text = s ?? '';
    return Math.ceil(this._measureText!.width);
  }

  private ellipsizeToPx(text: string, maxPx: number, style: Partial<PIXI.TextStyle>): string {
    if (!text) return '';
    this.ensureMeasure(style);

    if (this.textWidthPx(text) <= maxPx) return text;

    const ell = '…';
    const ellW = this.textWidthPx(ell);
    if (ellW > maxPx) return '';

    // Find the maximum match length using binary search
    let lo = 0, hi = text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const cand = text.slice(0, mid) + ell;
      if (this.textWidthPx(cand) <= maxPx) lo = mid;
      else hi = mid - 1;
    }
    return text.slice(0, lo) + ell;
  }

  private getLabelFromPool(index: number, style: Partial<PIXI.TextStyle>): PIXI.Text {
    if (this._svLabelPool[index]) return this._svLabelPool[index];
    const t = new PIXI.Text({ text: '', style: style });
    t.anchor.set(0.5, 0.5);
    t.roundPixels = true;
    (t as any).resolution = (this.app.renderer as any).resolution ?? 1;
    this.hudSVLabels.addChild(t);
    this._svLabelPool[index] = t;
    return t;
  }

  private hideUnusedLabelsFromPool(fromIndex: number) {
    for (let i = fromIndex; i < this._svLabelPool.length; i++) {
      const t = this._svLabelPool[i];
      if (t) t.visible = false;
    }
  }

  private clickEvent(e: PIXI.FederatedPointerEvent, chr: string, outOfBounds?: boolean): void {
    const bp = this.screenXToBp(e.global.x);

    // skip clicks during / immediately after dragging
    const now = performance.now ? performance.now() : Date.now();
    const inDragCooldown = (now - this._lastDragEndedAt) <= this._dragCooldownMs;
    if (this._isDragging || inDragCooldown) {
      if (this._singleClickTimer != null) {
        clearTimeout(this._singleClickTimer);
        this._singleClickTimer = undefined;
      }
      return;
    }

    if (e.shiftKey && !outOfBounds) {
      if (this._singleClickTimer != null) {
        clearTimeout(this._singleClickTimer);
        this._singleClickTimer = undefined;
      }
      this.hceService.openUcscGenomeBrowser(this.db, chr, bp);
      return;
    }

    // Distance guard (if the movement distance from the press position is large, it will not be treated as a click)
    const movedPx = Math.hypot(e.global.x - this._dragStartPos.x, e.global.y - this._dragStartPos.y);
    if (movedPx > this._dragMinDist) {
      if (this._singleClickTimer != null) {
        clearTimeout(this._singleClickTimer);
        this._singleClickTimer = undefined;
      }
      return;
    }

    // Double-click Detection (Delayed Confirmation Method)
    const withinTime = (now - this._lastClickAt) <= this._dblClickMs;
    const withinDist = Math.hypot(e.global.x - this._lastClickPos.x, e.global.y - this._lastClickPos.y) <= this._dblClickDist;

    if (withinTime && withinDist) {
      if (this._singleClickTimer != null) {
        clearTimeout(this._singleClickTimer);
        this._singleClickTimer = undefined;
      }
      this.onDoubleClick(chr, bp, e);
      this._lastClickAt = 0;
      return;
    }

    if (this._singleClickTimer != null) {
      clearTimeout(this._singleClickTimer);
    }
    this._singleClickTimer = window.setTimeout(() => {
      // Final check to ensure no dragging has occurred during the delay
      const now2 = performance.now ? performance.now() : Date.now();
      if (!this._isDragging && (now2 - this._lastDragEndedAt) > this._dragCooldownMs && !outOfBounds) {
        this.onSingleClick(chr, bp, e);
      }
      this._singleClickTimer = undefined;
    }, this._dblClickMs);

    this._lastClickAt = now;
    this._lastClickPos.copyFrom(e.global);
  }

  private onSingleClick(chr: string, bp: number, e: PIXI.FederatedPointerEvent) {
    const payload = {
      analysisId: this.analysis?.AnalysisID,
      db: this.db,
      bp: bp,
      range: 3,
      chromosomeName: chr,
      alignmentScale: "scale1",
      annotationFile: Object.keys(this._annotations),
    } as HceAlignmentViewRequest;
    this.hceService.openAlignmentViewer(payload, "_blank");
  }

  private onDoubleClick(chr: string, bp: number, e: PIXI.FederatedPointerEvent) {
    this.initScale();
    this.hideTooltip();
  }

  // Add text with abbreviation and tooltip to the left band HUD, and return PIXI.Text
  private createHudEllipsizedText(opts: {
    fullText: string;                 // Text before omission (also used for tooltip)
    worldY: number;                   // World coordinate Y (center-based)
    style: Partial<PIXI.TextStyle>;   // Text style
    leftPad?: number;                 // Left padding (px)
    rightPad?: number;                // Right padding (px)
    cursor?: string;                  // 'help', etc. (optional)
  }): PIXI.Text | null {
    const LEFT_PAD = Math.max(0, opts.leftPad ?? 3);
    const RIGHT_PAD = Math.max(0, opts.rightPad ?? 3);
    const maxPx = Math.max(0, this.Label_X - LEFT_PAD - RIGHT_PAD);

    const shown = this.ellipsizeCached(opts.fullText, maxPx, opts.style);
    if (!shown) return null;

    // worldY -> screenY
    const sp = this.viewport.toScreen(0, opts.worldY);
    const screenY = sp ? Math.round(sp.y) : 0;

    // textnode
    const txt = new PIXI.Text({ text: shown, style: opts.style });
    txt.anchor.set(0, 0.5);
    txt.position.set(LEFT_PAD, screenY);
    txt.roundPixels = true;
    (txt as any).resolution = (this.app.renderer as any).resolution ?? 1;

    // tooltip
    txt.eventMode = 'static';
    if (opts.cursor) txt.cursor = opts.cursor;
    txt.on('pointermove', (e: PIXI.FederatedPointerEvent) => {
      this.showTooltip([opts.fullText]);
      this.moveTooltip(e.global.x, e.global.y);
    });
    txt.on('pointerout', () => this.hideTooltip());

    this.hudFixed.addChild(txt);
    return txt;
  }

  private rebuildSvIndex(): void {
    this._svByChr.clear();

    for (const sv of this._svList) {
      const chrId = Number(sv.chr_id);
      if (!Number.isFinite(chrId)) continue;

      let list = this._svByChr.get(chrId);
      if (!list) {
        list = [];
        this._svByChr.set(chrId, list);
      }
      list.push(sv);
    }
  }

  private rebuildSvHoverIndex(): void {
    this._svHoverByChr.clear();

    const edgeStart = (v: EdgeVal | undefined) => this.rsvService.edgeKb(v, 'start');
    const edgeEnd = (v: EdgeVal | undefined) => this.rsvService.edgeKb(v, 'end');

    const svidNum = (s: string | null | undefined) => {
      const n = parseInt(String(s ?? '').replace(/\D/g, ''), 10);
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    };

    const rankForOther = (svType?: string | null) => {
      if (svType === 'Translocation') return this._svTypeRank['Translocation_origin'];
      return this._svTypeRank[String(svType ?? '')] ?? this._svTypeDefaultRank;
    };

    const rankForTransDest = () => this._svTypeRank['Translocation_destination'];

    const addEntry = (chrId: number, entry: SvHoverEntry) => {
      let list = this._svHoverByChr.get(chrId);
      if (!list) {
        list = [];
        this._svHoverByChr.set(chrId, list);
      }
      list.push(entry);
    };

    const buildCommonParts = (opts: {
      svid?: string | null | undefined;
      typeLabel?: string | null | undefined;
      atBp?: number;
      svLenKb?: number | null | undefined;
      refLenKb?: number | null | undefined;
      conLenKb?: number | null | undefined;
      quality?: string | null | undefined;
      ont_cohort?: string | null | undefined;
      reason?: string | null | undefined;
    }) => {
      const nf = this._tooltipNumberFormat;
      const parts: string[] = [];

      if (opts.svid && opts.typeLabel && typeof opts.atBp === 'number') {
        const id = parseInt(String(opts.svid).replace(/\D/g, ''), 10);
        parts.push(`SVID: ${id} ${opts.typeLabel} @${nf.format(opts.atBp || 0)}bp`);
      }
      if (opts.svLenKb != null) parts.push(`SVLen: ${nf.format(opts.svLenKb * 1000)}bp`);
      if (opts.refLenKb != null) parts.push(`RefLen: ${nf.format(opts.refLenKb * 1000)}bp`);
      if (opts.conLenKb != null) parts.push(`ConLen: ${nf.format(opts.conLenKb * 1000)}bp`);
      if (opts.quality != null) parts.push(`Quality score: ${opts.quality}`);
      if (opts.ont_cohort != null) parts.push(`ONT Cohort: ${opts.ont_cohort}%`);
      if (opts.reason != null) parts.push(`Reason: ${opts.reason}`);

      return parts.join(' / ');
    };

    for (const sv of this._svList) {
      const chrId = Number(sv.chr_id);
      const chrToId = Number(sv.chr_to_id);
      const svIdNum = svidNum(sv.hce_sv_id);

      // ===== standard side (same-chr / translocation origin) =====
      if (Number.isFinite(chrId)) {
        const svStartKb = edgeStart(sv?.region?.chr?.start as EdgeVal | undefined);
        const svEndKb = edgeEnd(sv?.region?.chr?.end as EdgeVal | undefined);

        const SV_ConSize = sv?.region?.contig?.length as number;
        const mdw = SV_ConSize / 2;
        const c = (svStartKb + svEndKb) / 2;
        
        const minKb = Math.min(svStartKb, c - mdw);
        const maxKb = Math.max(svEndKb, c + mdw);
        const quality_score = sv.quality_score ? String(sv.quality_score).substring(0,4) : "undefined";
        const ont_cohort = sv.lytx?.percent_presence_ont_cohort;
        const disp_ont_cohort = ont_cohort !== undefined && ont_cohort !== null ? ont_cohort.toFixed(2) : null;

        const message = buildCommonParts({
          svid: sv.hce_sv_id,
          typeLabel: sv.type !== 'Translocation' ? `(${sv.type})` : `(${sv.type} origin)`,
          atBp: svStartKb * 1000,
          svLenKb: sv.length ?? null,
          refLenKb: sv?.region?.chr?.length ?? null,
          conLenKb: sv?.region?.contig?.length ?? null,
          quality: quality_score,
          ont_cohort: disp_ont_cohort,
          reason: sv.type !== 'Translocation' ? (sv?.reason ?? '') : '',
        });

        let destinationBlock = '';
        if (sv.type === 'Translocation') {
          const segToStartKb = edgeStart(sv?.segment_to?.start as EdgeVal | undefined);
          const segToEndKb = edgeEnd(sv?.segment_to?.end as EdgeVal | undefined);
          const segRefSize = Math.max(segToStartKb, segToEndKb) - Math.min(segToStartKb, segToEndKb);

          const destinationParts: string[] = [];
          const nf = this._tooltipNumberFormat;

          destinationParts.push(
            `@${nf.format(segToStartKb * 1000)}bp`,
            ...(sv?.segment_to?.length ? [`SVLen: ${nf.format(sv?.segment_to?.length * 1000)}bp`] : []),
            ...(segRefSize ? [`RefLen: ${nf.format(segRefSize * 1000)}bp`] : []),
            ...(sv?.region?.contig?.length ? ['ConLen: 0bp'] : []),
          );

          destinationBlock = `\r(destination) ${destinationParts.join(' ')}`;
        }

        const genes = sv?.region?.chr?.affected_genes;
        const genesBlock = genes ? this.rsvService.collectGenes(genes) : '';

        addEntry(chrId, {
          minKb,
          maxKb,
          rank: rankForOther(sv.type),
          svid: svIdNum,
          block: `${message}${destinationBlock}${genesBlock}`,
        });
      }

      // ===== translocation destination =====
      if (sv.type === 'Translocation' && Number.isFinite(chrToId)) {
        const segToStartKb = edgeStart(sv?.segment_to?.start as EdgeVal | undefined);
        const segToEndKb = edgeEnd(sv?.segment_to?.end as EdgeVal | undefined);
        const segRefSize = Math.max(segToStartKb, segToEndKb) - Math.min(segToStartKb, segToEndKb);

        const destMinKb = Math.min(segToStartKb, segToEndKb);
        const destMaxKb = Math.max(segToStartKb, segToEndKb);

        const destLine = buildCommonParts({
          svid: sv.hce_sv_id,
          typeLabel: `(${sv.type} destination)`,
          atBp: segToStartKb * 1000,
          svLenKb: sv?.segment_to?.length ?? null,
          refLenKb: segRefSize,
          conLenKb: 0,
          quality: null,
          reason: null,
        });

        const svStartKb = edgeStart(sv?.region?.chr?.start as EdgeVal | undefined);

        const originParts: string[] = [];
        const nf = this._tooltipNumberFormat;

        originParts.push('(origin) ');
        if (sv.chr_id) {
          originParts.push(`${this.rsvService.objectFindValue(this.refsMappingTable, ([k]) => Number(k) == sv.chr_id)}`);
        }
        originParts.push(
          `@${nf.format(svStartKb * 1000)}bp`,
          ...(sv.length ? [`SVLen: ${nf.format(sv.length * 1000)}bp`] : []),
          ...(sv?.region?.chr?.length ? [`RefLen: ${nf.format(sv.region.chr.length * 1000)}bp`] : []),
          ...(sv?.region?.contig?.length ? [`ConLen: ${nf.format(sv.region.contig.length * 1000)}bp`] : []),
        );

        const originLine = originParts.join(' ');
        const genes = sv?.segment_to?.affected_genes;
        const genesBlock = genes ? this.rsvService.collectGenes(genes) : '';

        addEntry(chrToId, {
          minKb: destMinKb,
          maxKb: destMaxKb,
          rank: rankForTransDest(),
          svid: svIdNum,
          block: `${destLine}\r${originLine}${genesBlock}`,
        });
      }
    }

    for (const list of this._svHoverByChr.values()) {
      list.sort((a, b) => (a.rank - b.rank) || (a.svid - b.svid));
    }
  }
}